import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import ReactSelect from 'react-select';
import itemService from '../../services/ItemService';
import { useFetchQboAccounts } from '../MyTime/hooks/useFetchQboAccounts';

const ITEM_TYPES = [
  { value: 'Service', label: 'Service / expense bucket (BP-95xxx)' },
  { value: 'Inventory', label: 'Inventory item' },
  { value: 'NonInventory', label: 'Non-inventory item' },
  { value: 'Group', label: 'Bundle' }
];

const refOption = (reference) => ({
  value: String(reference.value),
  label: reference.name
});

const selectedOption = (options, reference) =>
  options.find((option) => String(option.value) === String(reference?.value || '')) || null;

const optionToRef = (option) => (option ? { value: String(option.value), name: option.label } : null);

const CreateBoxshallProductDialog = ({
  open,
  initialName = '',
  lineItem = {},
  classRefs = [],
  taxCodes = [],
  vendorList = [],
  defaultAccounts = null,
  defaultTaxCode = null,
  onPreview,
  onCancel,
  onSave
}) => {
  const { qboAccounts, qboAccountLoading, qboAccountError } = useFetchQboAccounts();
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [reusingProduct, setReusingProduct] = useState(false);
  const previewRequestRef = useRef(0);
  const [form, setForm] = useState(() => ({
    Name: '',
    Type: 'NonInventory',
    ParentRef: null,
    ClassRef: lineItem?.classRef || null,
    Active: true,
    Description:
      initialName ||
      lineItem?.boxshallDescription ||
      lineItem?.productServiceDescription ||
      lineItem?.description ||
      '',
    UnitPrice: lineItem?.productServiceRate ?? lineItem?.unitPrice?.amount ?? '',
    IncomeAccountRef: defaultAccounts?.incomeAccount || null,
    SalesTaxCodeRef: lineItem?.taxCode || defaultTaxCode || null,
    PurchaseDesc:
      initialName ||
      lineItem?.boxshallDescription ||
      lineItem?.productServiceDescription ||
      lineItem?.description ||
      '',
    PurchaseCost: lineItem?.productServiceRate ?? lineItem?.unitPrice?.amount ?? '',
    ExpenseAccountRef: defaultAccounts?.expenseAccount || null,
    PrefVendorRef: null,
    PurchaseTaxCodeRef: lineItem?.taxCode || defaultTaxCode || null
  }));

  useEffect(() => {
    let active = true;

    const fetchCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError('');
      try {
        const pageSize = 1000;
        let skip = 0;
        let totalCount = pageSize;
        const allItems = [];

        while (skip < totalCount) {
          const response = await itemService.getItemsFromQBO({
            take: pageSize,
            skip,
            type: 'Category'
          });
          const items = response?.data?.data?.QueryResponse?.Item || [];
          allItems.push(...items);
          totalCount = Number(response?.data?.newTotalCount) || allItems.length;
          if (!items.length) break;
          skip += items.length;
        }

        if (active) {
          setCategories(
            allItems
              .filter((item) => item.Type === 'Category' && item.Active !== false)
              .map((item) => ({ value: String(item.Id), label: item.name || item.Name }))
          );
        }
      } catch (error) {
        if (active) {
          setCategoriesError(error?.response?.data?.message || error?.message || 'Could not load QBO categories');
        }
      } finally {
        if (active) setCategoriesLoading(false);
      }
    };

    if (open) fetchCategories();
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    const description = form.Description.trim();
    setForm((current) => (current.Name ? { ...current, Name: '' } : current));
    setReusingProduct(false);
    setPreviewError('');
    if (!description) {
      setPreviewLoading(false);
      return undefined;
    }

    setPreviewLoading(true);
    // eslint-disable-next-line no-undef
    const timeout = window.setTimeout(async () => {
      try {
        const preview = await onPreview({
          Description: description,
          Type: form.Type,
          ParentRef: form.ParentRef
        });
        if (previewRequestRef.current !== requestId) return;
        setForm((current) => ({ ...current, Name: preview.code || '' }));
        setReusingProduct(Boolean(preview.reused));
        setPreviewError(preview.requiresCategory ? 'Select a category to generate the BP code' : '');
      } catch (error) {
        if (previewRequestRef.current !== requestId) return;
        setPreviewError(error?.response?.data?.message || error?.message || 'Could not generate the BP code');
      } finally {
        if (previewRequestRef.current === requestId) setPreviewLoading(false);
      }
    }, 350);

    return () => {
      // eslint-disable-next-line no-undef
      window.clearTimeout(timeout);
    };
  }, [form.Description, form.ParentRef, form.Type, onPreview]);

  const accountOptions = useMemo(() => qboAccounts.map(refOption), [qboAccounts]);
  const classOptions = useMemo(() => classRefs.map(refOption), [classRefs]);
  const taxCodeOptions = useMemo(() => taxCodes.map(refOption), [taxCodes]);
  const vendorOptions = useMemo(
    () =>
      vendorList
        .map((vendor) => ({
          value: String(vendor.id || vendor.value || ''),
          label: vendor.label || vendor.name || vendor.value
        }))
        .filter((option) => option.value && option.label),
    [vendorList]
  );

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const save = async () => {
    const Name = form.Name.trim();
    if (saving || previewLoading || !Name) return;

    setSaving(true);
    try {
      await onSave({
        ...form,
        Name,
        UnitPrice: form.UnitPrice === '' ? 0 : Number(form.UnitPrice),
        PurchaseCost: form.PurchaseCost === '' ? 0 : Number(form.PurchaseCost)
      });
    } finally {
      setSaving(false);
    }
  };

  const selectProps = {
    isClearable: true,
    isSearchable: true,
    // eslint-disable-next-line no-undef
    menuPortalTarget: document.body,
    menuPosition: 'fixed',
    styles: { menuPortal: (base) => ({ ...base, zIndex: 1400 }) }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} fullWidth maxWidth="md">
      <DialogTitle>Create Boxshall Product</DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#f3f4f6' }}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Basic info
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Name"
              value={form.Name}
              error={Boolean(previewError)}
              helperText={
                previewLoading
                  ? 'Checking existing products and generating the BP code…'
                  : previewError ||
                    (reusingProduct
                      ? 'An existing matching product will be reused.'
                      : 'Generated by Verd from the selected category.')
              }
              InputProps={{ readOnly: true }}
              required
              fullWidth
            />
            <TextField
              select
              label="Item type"
              value={form.Type}
              onChange={(event) => updateField('Type', event.target.value)}
              fullWidth
            >
              {ITEM_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <div>
              <Typography variant="caption" color="text.secondary">
                Category
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-item-category"
                aria-label="Category"
                isLoading={categoriesLoading}
                options={categories}
                value={selectedOption(categories, form.ParentRef)}
                onChange={(option) => updateField('ParentRef', optionToRef(option))}
                placeholder="Select a category"
              />
              {categoriesError && (
                <Typography variant="caption" color="error">
                  {categoriesError}
                </Typography>
              )}
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">
                Class
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-item-class"
                aria-label="Class"
                options={classOptions}
                value={selectedOption(classOptions, form.ClassRef)}
                onChange={(option) => updateField('ClassRef', optionToRef(option))}
                placeholder="Select a class"
              />
            </div>
          </div>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox checked={form.Active} onChange={(event) => updateField('Active', event.target.checked)} />
            }
            label="Active"
          />
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Sales
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Description"
              value={form.Description}
              onChange={(event) => updateField('Description', event.target.value)}
              multiline
              minRows={2}
              className="md:tw-col-span-2"
              fullWidth
            />
            <TextField
              label="Price/rate"
              type="number"
              value={form.UnitPrice}
              onChange={(event) => updateField('UnitPrice', event.target.value)}
              inputProps={{ step: '0.01' }}
              fullWidth
            />
            <div>
              <Typography variant="caption" color="text.secondary">
                Income account
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-item-income-account"
                aria-label="Income account"
                isLoading={qboAccountLoading}
                options={accountOptions}
                value={selectedOption(accountOptions, form.IncomeAccountRef)}
                onChange={(option) => updateField('IncomeAccountRef', optionToRef(option))}
                placeholder="Select an income account"
              />
            </div>
            <div className="md:tw-col-span-2">
              <Typography variant="caption" color="text.secondary">
                Sales tax
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-item-sales-tax"
                aria-label="Sales tax"
                options={taxCodeOptions}
                value={selectedOption(taxCodeOptions, form.SalesTaxCodeRef)}
                onChange={(option) => updateField('SalesTaxCodeRef', optionToRef(option))}
                placeholder="Select a sales tax code"
              />
            </div>
          </div>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Purchasing
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Purchase description"
              value={form.PurchaseDesc}
              onChange={(event) => updateField('PurchaseDesc', event.target.value)}
              multiline
              minRows={2}
              className="md:tw-col-span-2"
              fullWidth
            />
            <TextField
              label="Purchase cost"
              type="number"
              value={form.PurchaseCost}
              onChange={(event) => updateField('PurchaseCost', event.target.value)}
              inputProps={{ step: '0.01' }}
              fullWidth
            />
            <div>
              <Typography variant="caption" color="text.secondary">
                Expense account
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-item-expense-account"
                aria-label="Expense account"
                isLoading={qboAccountLoading}
                options={accountOptions}
                value={selectedOption(accountOptions, form.ExpenseAccountRef)}
                onChange={(option) => updateField('ExpenseAccountRef', optionToRef(option))}
                placeholder="Select an expense account"
              />
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">
                Preferred supplier
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-item-preferred-supplier"
                aria-label="Preferred supplier"
                options={vendorOptions}
                value={selectedOption(vendorOptions, form.PrefVendorRef)}
                onChange={(option) => updateField('PrefVendorRef', optionToRef(option))}
                placeholder="Select a preferred supplier"
              />
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">
                Purchase tax
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-item-purchase-tax"
                aria-label="Purchase tax"
                options={taxCodeOptions}
                value={selectedOption(taxCodeOptions, form.PurchaseTaxCodeRef)}
                onChange={(option) => updateField('PurchaseTaxCodeRef', optionToRef(option))}
                placeholder="Select a purchase tax code"
              />
            </div>
          </div>
          {qboAccountError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {qboAccountError}
            </Typography>
          )}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={save} variant="contained" disabled={saving || previewLoading || !form.Name.trim()}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateBoxshallProductDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialName: PropTypes.string,
  lineItem: PropTypes.object,
  classRefs: PropTypes.array,
  taxCodes: PropTypes.array,
  vendorList: PropTypes.array,
  defaultAccounts: PropTypes.object,
  defaultTaxCode: PropTypes.object,
  onPreview: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default CreateBoxshallProductDialog;
